import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  forwardRef,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';

@Component({
  selector: 'date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true
    }
  ]
})
export class DateInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() isRequired = true;
  @Input() label = '';
  @Input() labelPadding = '';
  @Input() country: 'US' | 'CA' = 'US';
  @Input() hasFeedback = false;
  @ViewChild('yearInput') yearInput: NzInputNumberComponent;
  @ViewChild('monthInput') monthInput: NzInputNumberComponent;
  @ViewChild('dayInput') dayInput: NzInputNumberComponent;

  constructor(private fb: FormBuilder) {}
  FG = this.fb.group({
    year: ['', [Validators.required, Validators.min(1900)]],
    month: ['', [Validators.required, Validators.min(1), Validators.max(12)]],
    day: ['', [Validators.required, Validators.min(1), Validators.max(31)]]
  });

  active = true;

  get isTouched() {
    return (
      this.FG.get('year').touched &&
      this.FG.get('month').touched &&
      this.FG.get('day').touched
    );
  }

  get isDirty() {
    return (
      this.FG.get('year').dirty &&
      this.FG.get('month').dirty &&
      this.FG.get('day').dirty
    );
  }

  ngOnInit(): void {
    this.FG.valueChanges
      .pipe(debounceTime(500))
      .subscribe(({ year, month, day }) => {
        try {
          // set value as empty when all fields are empty
          if (!year && !month && !day) {
            this.setValue(null);
            return;
          }

          const isValidDate = this.validate(year, month, day);
          if (isValidDate) {
            const date = this.format(year, month, day);
            this.setValue(date);
          } else {
            this.setValue('Invalid Date');
          }
        } catch (error) {
          console.error('date-input:: value change error', error);
        }
      });
  }

  ngOnDestroy() {
    this.active = false;
  }

  yearParser = (value: number) => {
    return value;
  };

  monthParser = (value: number) => {
    if (String(value).length === 2) {
      if (this.country === 'CA') setTimeout(() => this.yearInput.focus(), 100);
      if (this.country === 'US') setTimeout(() => this.dayInput.focus(), 100);
    }
    return value;
  };

  dayParser = (value: number) => {
    if (String(value).length === 2) {
      if (this.country === 'CA') {
        console.log('yellow');
        setTimeout(() => this.monthInput.focus(), 100);
      }
      if (this.country === 'US') {
        console.log('a');
        setTimeout(() => this.yearInput.focus(), 100);
      }
    }
    return value;
  };

  validate(yearStr: string, monthStr: string, dayStr: string) {
    const year = +yearStr;
    const month = +monthStr;
    const day = +dayStr;
    // Check the ranges of month and year
    if (
      !year ||
      year < 1000 ||
      year > 3000 ||
      !month ||
      month > 12 ||
      month < 0
    )
      return false;

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
      monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
  }

  /* -------------------------------------------------------------------------- */
  /*                       Control Value Accessor methods                       */
  /* -------------------------------------------------------------------------- */

  val: string = null;
  onChange: (v: string) => void;
  onTouch: (v: string) => void;

  setValue(v: string) {
    // this value is updated by programmatic changes
    this.val = v;
    if (v == null && !this.isTouched) {
      return; // prevent showing invalid state when element is not touched yet
    }
    this.onChange && this.onChange(v);

    // register touched when all controls are touched
    this.isTouched && this.onTouch && this.onTouch(v);
  }

  // this method sets the value programmatically
  writeValue(value: string | Date) {
    if (!value) {
      this.setValue(null);
      return;
    }

    // convert to yyyy-MM-dd
    if (value instanceof Date) {
      value = value.toISOString();
    }
    value = value.substring(0, 10);
    const [year, month, day] = value.split('-');
    this.FG.patchValue({ year, month, day }, { emitEvent: false });

    if (!this.validate(year, month, day)) {
      this.setValue('Invalid Date');
      return;
    }

    this.setValue(value);
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.FG.disable();
    } else {
      this.FG.enable();
    }
  }

  format(year: string, month: string, day: string) {
    const yyyy = year + '';
    const MM = (month + '').length === 2 ? month + '' : `0${month}`;
    const dd = (day + '').length === 2 ? day + '' : `0${day}`;
    return `${yyyy}-${MM}-${dd}`;
  }
}
